import axios, { AxiosRequestConfig, AxiosError } from "axios";
import { toast } from "react-toastify";
import type { ApiParamsType } from "../../types/api";
import serialize from "./serialize";
import ApiMap from "./map";
import ApiException from "../../types/api";
import { toastifyError } from "../../utils/toastify-message";
import { sentryLogException } from "services/sentryLogger";

const HTTP_ERROR_TOAST_ID = "http-error-toast";

class HTTP {
  organizationId = "";
  globalError = "";
  timeout = 0;

  instance = axios.create({
    baseURL: `${import.meta.env.VITE_API_ENDPOINT || ""}/api/v2/`,
    withCredentials: true,
  });

  constructor() {
    // Add response interceptor to handle HTTP errors globally
    this.instance.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        this.handleHttpError(error);
        return Promise.reject(error);
      },
    );
  }

  private handleHttpError(error: AxiosError): void {
    const statusCode = error.response?.status || error.status || "Unknown";
    let errorMessage = "An error occurred";

    // Extract error message from response
    if (error.response?.data) {
      const data = error.response.data;
      if (typeof data === "string") {
        errorMessage = data;
      } else if (data && typeof data === "object") {
        // Try common error message fields
        errorMessage =
          (data as any).message ||
          (data as any).error ||
          (data as any).detail ||
          (data as any).errorMessage ||
          (data as any).error_description ||
          (typeof (data as any).data === "string"
            ? (data as any).data
            : null) ||
          "An error occurred";

        // If we couldn't find a message, try to stringify but limit length
        if (errorMessage === "An error occurred") {
          try {
            const stringified = JSON.stringify(data);
            errorMessage =
              stringified.length > 200
                ? stringified.substring(0, 200) + "..."
                : stringified;
          } catch {
            errorMessage = "An error occurred";
          }
        }
      }
    } else if (error.message) {
      errorMessage = error.message;
    } else if (error.code === "ECONNABORTED" || error.code === "ETIMEDOUT") {
      errorMessage = "Request timeout - please check your connection";
    } else if (error.code === "ERR_NETWORK") {
      errorMessage = "Network error - please check your connection";
    }

    // Display error notification only if no notification is currently active
    if (!toast.isActive(HTTP_ERROR_TOAST_ID)) {
      const notificationMessage = `Error ${statusCode}: ${errorMessage}`;
      toastifyError(notificationMessage, {
        toastId: HTTP_ERROR_TOAST_ID,
        autoClose: 5000,
      });
    }
  }

  public initHttpService(orgId: string) {
    this.organizationId = orgId;
  }

  public setGlobalError(error: string) {
    this.globalError = error;
  }

  public setTimeout(timeout: number) {
    this.timeout = timeout;
  }

  private getRequestConfig({
    key,
    data,
    params,
    queryParams,
    otherApiConfig,
    extraHeader,
  }: ApiParamsType): AxiosRequestConfig<any> {
    const {
      url = "",
      method,
      noCredentials,
    } = otherApiConfig || ApiMap[key] || {};
    return {
      headers: {
        ...(extraHeader || {}),
      },
      url: serialize(url || "", params),
      timeout: this.timeout,
      method,
      params: queryParams,
      data,
      withCredentials: noCredentials ? false : undefined,
    };
  }

  public async request<TResponse>(
    apiParams: ApiParamsType,
  ): Promise<TResponse> {
    try {
      if (ApiMap[apiParams.key].mock) {
        const res: any = {
          url: "",
          timeout: this.timeout,
          method: "get",
          params: {},
          data: ApiMap[apiParams.key].mock(),
        };
        return res.data;
      }
      const response = await this.instance<TResponse>(
        this.getRequestConfig(apiParams),
      );
      return response.data;
    } catch (error: any) {
      console.error({ error });
      sentryLogException({
        error,
        tags: {
          component: "http",
          category: "request",
        },
        extra: {
          apiKey: apiParams.key,
          statusCode: error.response?.status || error.status,
          method: this.getRequestConfig(apiParams).method,
          url: this.getRequestConfig(apiParams).url,
          timestamp: new Date().toISOString(),
        },
      });
      throw {
        message: error.message,
        statusCode: error.response?.status || error.status,
        status: false,
        data: error.response?.data || this.globalError,
        name: error.name,
      } as ApiException;
    }
  }
}

const http = new HTTP();
export default http;
